import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, LayoutGrid, List, MoreHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import {
  audienceDraftFromTargets,
  audienceTargetsFromDraft,
  displayVisibility,
  DocumentVisibilityFields,
  emptyAudienceDraft,
  isAudienceReady,
  type DocumentAudienceDraft,
  type DocumentAudienceTarget,
  type DocumentVisibility,
} from '@/components/documents/document-visibility-fields';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { uploadBlob } from '@/lib/upload';
import { cn } from '@/utils/cn';
import { churchToday, formatDate } from '@/utils/format';

type DocumentStatus = 'draft' | 'published' | 'archived';

interface DocumentCategory {
  id: string;
  name: string;
  isSystem: boolean;
  sortOrder: number;
  visibility: DocumentVisibility;
  audienceTargets?: DocumentAudienceTarget[];
  documentCount: number;
}

interface DocumentRow {
  id: string;
  title: string;
  categoryId: string;
  categoryName: string;
  documentDate: string | null;
  status: DocumentStatus;
  fileName: string;
}

type CategoryView = 'grid' | 'list';

type CategoryRow = DocumentCategory;

export const DocumentsPage = () => {
  const { t } = useTranslation('documents');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const canCreate = hasPermission(membership, PERMISSIONS.DOCUMENTS_CREATE);
  const canEdit = hasPermission(membership, PERMISSIONS.DOCUMENTS_EDIT);
  const canManage = hasPermission(membership, PERMISSIONS.DOCUMENTS_MANAGE);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategoryId = searchParams.get('category');
  const [categoryView, setCategoryView] = useState<CategoryView>('list');
  const [fileSearch, setFileSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DocumentRow | null>(null);
  const [folderOpen, setFolderOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<DocumentCategory | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<DocumentCategory | null>(null);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [folderName, setFolderName] = useState('');
  const [folderVisibility, setFolderVisibility] = useState<DocumentVisibility>('staff');
  const [folderAudience, setFolderAudience] = useState<DocumentAudienceDraft>(emptyAudienceDraft);
  const [visibility, setVisibility] = useState<DocumentVisibility>('staff');
  const [audience, setAudience] = useState<DocumentAudienceDraft>(emptyAudienceDraft);
  const [documentDate, setDocumentDate] = useState<string | null>(churchToday());
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);

  const categories = useQuery({
    queryKey: ['documents', 'categories'],
    queryFn: () =>
      api.get<{ data: DocumentCategory[]; page: OffsetPage }>('/documents/categories', {
        query: {
          page: 1,
          pageSize: 100,
        },
      }),
  });

  const categoryList = categories.data?.data ?? [];
  const selectedCategory = categoryList.find((item) => item.id === selectedCategoryId) ?? null;

  useEffect(() => {
    if (
      selectedCategoryId &&
      !categories.isLoading &&
      categoryList.length > 0 &&
      !selectedCategory
    ) {
      setSearchParams({});
    }
  }, [selectedCategoryId, categories.isLoading, categoryList.length, selectedCategory, setSearchParams]);

  useEffect(() => {
    setPage(1);
    setFileSearch('');
  }, [selectedCategoryId]);

  const fileSearchActive = fileSearch.trim().length > 0;
  const inCategory = Boolean(selectedCategoryId);

  const list = useQuery({
    queryKey: ['documents', selectedCategoryId ?? 'all', page, pageSize, fileSearch],
    enabled: inCategory || fileSearchActive,
    queryFn: () =>
      api.get<{ data: DocumentRow[]; page: OffsetPage }>('/documents', {
        query: {
          page,
          pageSize,
          categoryId: selectedCategoryId ?? undefined,
          q: fileSearch.trim() || undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const openCategory = (value: DocumentCategory | Pick<DocumentCategory, 'id'>) => {
    setSearchParams({ category: value.id });
    setPage(1);
  };

  const openCreate = () => {
    if (selectedCategoryId) setCategoryId(selectedCategoryId);
    else if (categoryList[0]) setCategoryId(categoryList[0].id);
    setOpen(true);
  };

  const resetForm = () => {
    setTitle('');
    setCategoryId(selectedCategoryId ?? categoryList[0]?.id ?? '');
    setVisibility('staff');
    setAudience(emptyAudienceDraft());
    setDocumentDate(churchToday());
    setFile(null);
    setFileKey(null);
    setFileError(undefined);
  };

  const resetRenameForm = () => {
    setEditingDocument(null);
    setTitle('');
  };

  const openRenameDocument = (document: DocumentRow) => {
    setEditingDocument(document);
    setTitle(document.title);
    setRenameOpen(true);
  };

  const resetFolderForm = () => {
    setEditingCategory(null);
    setFolderName('');
    setFolderVisibility('staff');
    setFolderAudience(emptyAudienceDraft());
  };

  const openCreateFolder = () => {
    resetFolderForm();
    setFolderOpen(true);
  };

  const openEditFolder = (category: DocumentCategory) => {
    setEditingCategory(category);
    setFolderName(category.name);
    setFolderVisibility(displayVisibility(category.visibility));
    setFolderAudience(audienceDraftFromTargets(category.audienceTargets));
    setFolderOpen(true);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<DocumentRow>('/documents', {
        title: title.trim(),
        categoryId,
        visibility,
        audienceTargets: audienceTargetsFromDraft(visibility, audience),
        documentDate: documentDate ?? undefined,
        fileKey,
        fileName: file?.name,
        contentType: file?.type || 'application/octet-stream',
      }),
    onSuccess: async () => {
      toast.success(t('saved'));
      setOpen(false);
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const renameDocument = useMutation({
    mutationFn: (documentId: string) =>
      api.patch<DocumentRow>(`/documents/${documentId}`, {
        title: title.trim(),
      }),
    onSuccess: async () => {
      toast.success(t('updated'));
      setRenameOpen(false);
      resetRenameForm();
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const saveFolder = useMutation({
    mutationFn: () => {
      const payload = {
        name: folderName.trim(),
        visibility: folderVisibility,
        audienceTargets: audienceTargetsFromDraft(folderVisibility, folderAudience),
      };
      if (editingCategory) {
        return api.patch<DocumentCategory>(`/documents/categories/${editingCategory.id}`, payload);
      }
      return api.post<DocumentCategory>('/documents/categories', payload);
    },
    onSuccess: async () => {
      toast.success(editingCategory ? t('categories.saved') : t('categories.created'));
      setFolderOpen(false);
      resetFolderForm();
      await queryClient.invalidateQueries({ queryKey: ['documents', 'categories'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const removeFolder = useMutation({
    mutationFn: (id: string) => api.delete(`/documents/categories/${id}`),
    onSuccess: async () => {
      toast.success(t('categories.deleted'));
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const publish = useMutation({
    mutationFn: (documentId: string) =>
      api.post<DocumentRow>(`/documents/${documentId}/publish`),
    onSuccess: async () => {
      toast.success(t('published'));
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const archive = useMutation({
    mutationFn: (documentId: string) =>
      api.post<DocumentRow>(`/documents/${documentId}/archive`),
    onSuccess: async () => {
      toast.success(t('archived'));
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const onFiles = async (files: File[]) => {
    const next = files[0];
    if (!next) return;
    setFileError(undefined);
    setUploading(true);
    try {
      const { key } = await uploadBlob({
        purpose: 'church_document',
        blob: next,
        fileName: next.name,
      });
      setFile(next);
      setFileKey(key);
    } catch (error) {
      setFile(null);
      setFileKey(null);
      toast.error(resolveApiError(error, tErrors));
    } finally {
      setUploading(false);
    }
  };

  const renderDocumentActions = (row: DocumentRow) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={tCommon('table.actions')}
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEdit && row.status !== 'archived' ? (
          <DropdownMenuItem onClick={() => openRenameDocument(row)}>
            {t('rename')}
          </DropdownMenuItem>
        ) : null}
        {canManage && row.status === 'draft' ? (
          <DropdownMenuItem onClick={() => publish.mutate(row.id)}>
            {t('publish')}
          </DropdownMenuItem>
        ) : null}
        {canManage && row.status === 'published' ? (
          <DropdownMenuItem onClick={() => archive.mutate(row.id)}>
            {t('archive')}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const renderCategoryActions = (row: DocumentCategory) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={tCommon('table.actions')}
        >
          <MoreHorizontal className="size-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => openEditFolder(row)}>
          {t('categories.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem destructive onClick={() => setDeleteFolder(row)}>
          {t('categories.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const categoryColumns: DataTableColumn<CategoryRow>[] = [
    {
      id: 'name',
      header: t('categories.columns.name'),
      cell: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
            <Folder className="size-4" aria-hidden />
          </span>
          <span className="font-medium text-fg">{row.name}</span>
        </div>
      ),
    },
    {
      id: 'documents',
      header: t('categories.columns.documents'),
      cell: (row) => t('categories.count', { count: row.documentCount }),
    },
    {
      id: 'visibility',
      header: t('categories.columns.visibility'),
      cell: (row) => <Badge variant="neutral">{t(`visibility.${row.visibility}`)}</Badge>,
    },
  ];

  const showCategoryColumn = !inCategory && fileSearchActive;

  const documentColumns: DataTableColumn<DocumentRow>[] = [
    {
      id: 'documentDate',
      header: t('columns.documentDate'),
      cell: (row) => (row.documentDate ? formatDate(row.documentDate) : '—'),
    },
    ...(showCategoryColumn
      ? [
          {
            id: 'category',
            header: t('columns.category'),
            cell: (row: DocumentRow) => (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 font-normal"
                onClick={(event) => {
                  event.stopPropagation();
                  openCategory({ id: row.categoryId });
                }}
              >
                {row.categoryName}
              </Button>
            ),
          } satisfies DataTableColumn<DocumentRow>,
        ]
      : []),
    {
      id: 'title',
      header: t('columns.title'),
      cell: (row) => row.title,
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => <Badge variant="neutral">{t(`status.${row.status}`)}</Badge>,
    },
    {
      id: 'actions',
      header: t('columns.actions'),
      cell: (row) =>
        canEdit || canManage ? (
          <div
            className="flex justify-end"
            onPointerDown={(event) => event.stopPropagation()}
          >
            {renderDocumentActions(row)}
          </div>
        ) : null,
    },
  ];

  const rows = list.data?.data ?? [];
  const pageMeta = list.data?.page;
  const canSubmit =
    title.trim().length > 0 &&
    Boolean(fileKey) &&
    Boolean(file) &&
    Boolean(categoryId) &&
    isAudienceReady(visibility, audience);
  const canSubmitFolder =
    folderName.trim().length > 0 && isAudienceReady(folderVisibility, folderAudience);
  const pageTitle = selectedCategory?.name ?? t('title');
  const breadcrumbs = inCategory
    ? [{ label: t('title'), to: '/documents' }, { label: selectedCategory?.name ?? '…' }]
    : undefined;

  const documentSearchEmpty = (
    <p className="py-4 text-center text-body-sm text-fg-secondary">{t('search.emptyFiltered')}</p>
  );

  const documentPagination =
    pageMeta && pageMeta.totalPages > 1 ? (
      <div className="mt-4">
        <Pagination
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      </div>
    ) : null;

  const viewToggle = (
    <div
      className="flex rounded-lg border border-border p-0.5"
      role="group"
      aria-label={t('view.label')}
    >
      <Button
        type="button"
        size="sm"
        variant={categoryView === 'grid' ? 'primary' : 'ghost'}
        aria-pressed={categoryView === 'grid'}
        aria-label={t('view.grid')}
        onClick={() => setCategoryView('grid')}
      >
        <LayoutGrid className="size-4" aria-hidden />
      </Button>
      <Button
        type="button"
        size="sm"
        variant={categoryView === 'list' ? 'primary' : 'ghost'}
        aria-pressed={categoryView === 'list'}
        aria-label={t('view.list')}
        onClick={() => setCategoryView('list')}
      >
        <List className="size-4" aria-hidden />
      </Button>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={pageTitle}
        description={inCategory ? t('categories.description') : t('description')}
        breadcrumbs={breadcrumbs}
        headerActions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!inCategory && canManage ? (
              <Button variant="outline" onClick={openCreateFolder}>
                {t('categories.add')}
              </Button>
            ) : null}
            {canCreate ? <Button onClick={openCreate}>{t('create')}</Button> : null}
            {!inCategory && !fileSearchActive ? viewToggle : null}
          </div>
        }
      />

      <div className="mb-4">
        <SearchInput
          value={fileSearch}
          onChange={(value) => {
            setFileSearch(value);
            setPage(1);
          }}
          placeholder={t('search.documents')}
          clearLabel={tCommon('search.clear')}
          className="max-w-md"
          aria-label={t('search.documents')}
        />
      </div>

      {!inCategory ? (
        fileSearchActive ? (
          <>
            <DataTable
              rows={rows}
              columns={documentColumns}
              rowKey={(row) => row.id}
              loading={list.isLoading}
              empty={documentSearchEmpty}
            />
            {documentPagination}
          </>
        ) : categoryView === 'grid' ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {categories.isLoading ? (
              <p className="text-body-sm text-fg-secondary">{t('categories.countLoading')}</p>
            ) : null}
            {categoryList.map((item) => (
              <div
                key={item.id}
                tabIndex={0}
                onClick={() => openCategory(item)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openCategory(item);
                  }
                }}
                className={cn(
                  'flex w-full cursor-pointer items-start gap-3 rounded-xl border border-border bg-surface p-4 text-left shadow-e1',
                  'transition-colors hover:border-brand/40 hover:bg-surface-raised focus-visible:outline-none',
                  'focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                )}
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                  <Folder className="size-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-body font-medium text-fg">{item.name}</span>
                  <span className="mt-0.5 flex items-center gap-2 text-body-sm">
                    <Badge variant="neutral">{t(`visibility.${item.visibility}`)}</Badge>
                    <span className="text-fg-secondary">
                      {t('categories.count', { count: item.documentCount })}
                    </span>
                    {canManage ? (
                      <span
                        className="ml-auto shrink-0"
                        onPointerDown={(event) => event.stopPropagation()}
                      >
                        {renderCategoryActions(item)}
                      </span>
                    ) : null}
                  </span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <DataTable
            rows={categoryList}
            columns={categoryColumns}
            rowKey={(row) => row.id}
            loading={categories.isLoading}
            onRowClick={(row) => openCategory(row)}
            rowActions={canManage ? renderCategoryActions : undefined}
          />
        )
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={documentColumns}
            rowKey={(row) => row.id}
            loading={list.isLoading}
            empty={
              fileSearchActive ? (
                documentSearchEmpty
              ) : (
              <div className="space-y-3 py-4 text-center">
                <p className="text-body font-medium">{t('categoryEmpty.title')}</p>
                <p className="text-body-sm text-fg-secondary">{t('categoryEmpty.description')}</p>
                {canCreate ? (
                  <div className="flex justify-end sm:justify-center">
                    <Button onClick={openCreate}>{t('empty.action')}</Button>
                  </div>
                ) : null}
              </div>
              )
            }
          />

          {documentPagination}
        </>
      )}

      <FormDialog
        open={renameOpen}
        onOpenChange={(next) => {
          setRenameOpen(next);
          if (!next) resetRenameForm();
        }}
        title={t('renameTitle')}
        size="sm"
        submitLabel={t('rename')}
        cancelLabel={tCommon('action.cancel')}
        submitting={renameDocument.isPending}
        submitDisabled={title.trim().length === 0}
        onSubmit={() => {
          if (!editingDocument) return;
          renameDocument.mutate(editingDocument.id);
        }}
      >
        <FormField label={t('fields.title')} required>
          {(field) => (
            <Input
              id={field.id}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>
      </FormDialog>

      <FormDialog
        open={folderOpen}
        onOpenChange={(next) => {
          setFolderOpen(next);
          if (!next) resetFolderForm();
        }}
        title={editingCategory ? t('categories.editTitle') : t('categories.addTitle')}
        size="md"
        submitLabel={editingCategory ? t('categories.save') : t('categories.add')}
        cancelLabel={tCommon('action.cancel')}
        submitting={saveFolder.isPending}
        submitDisabled={!canSubmitFolder}
        onSubmit={() => saveFolder.mutate()}
      >
        <FormField label={t('categories.name')} required>
          {(field) => (
            <Input
              id={field.id}
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <DocumentVisibilityFields
          visibility={folderVisibility}
          onVisibilityChange={setFolderVisibility}
          audience={folderAudience}
          onAudienceChange={setFolderAudience}
        />

        <p className="text-body-sm text-fg-secondary">{t('categories.systemHint')}</p>
      </FormDialog>

      <FormDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) resetForm();
        }}
        title={t('create')}
        submitLabel={t('save')}
        cancelLabel={tCommon('action.cancel')}
        submitting={create.isPending || uploading}
        submitDisabled={!canSubmit}
        size="lg"
        onSubmit={() => {
          if (!fileKey || !file) {
            setFileError(t('file.required'));
            return;
          }
          create.mutate();
        }}
      >
        <FormField label={t('fields.title')} required>
          {(field) => (
            <Input
              id={field.id}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('fields.category')} required>
          {(field) => (
            <Listbox
              id={field.id}
              value={categoryId}
              onChange={(value) => setCategoryId(value ?? '')}
              options={categoryList.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <FormField label={t('fields.documentDate')}>
          {(field) => (
            <DatePicker
              id={field.id}
              value={documentDate}
              onChange={setDocumentDate}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
            />
          )}
        </FormField>

        <DocumentVisibilityFields
          visibility={visibility}
          onVisibilityChange={setVisibility}
          audience={audience}
          onAudienceChange={setAudience}
        />

        <div className="pt-2">
          <FormField label={t('fields.file')} required error={fileError}>
            {() => (
              <FileDropzone
                onFiles={(files) => void onFiles(files)}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/jpeg,image/png,image/webp,application/pdf"
                acceptLabel={t('file.accept')}
                maxBytes={25 * 1024 * 1024}
                disabled={uploading}
                error={fileError}
                labels={{ prompt: t('file.prompt'), remove: t('file.remove') }}
                files={file ? [{ name: file.name }] : []}
                onRemove={() => {
                  setFile(null);
                  setFileKey(null);
                }}
              />
            )}
          </FormField>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={deleteFolder !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteFolder(null);
        }}
        title={t('categories.delete')}
        description={
          deleteFolder
            ? t('categories.deleteConfirmNamed', { name: deleteFolder.name })
            : t('categories.deleteConfirm')
        }
        confirmLabel={t('categories.delete')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeFolder.isPending}
        onConfirm={() => {
          if (!deleteFolder) return;
          removeFolder.mutate(deleteFolder.id, {
            onSuccess: () => setDeleteFolder(null),
          });
        }}
      />
    </div>
  );
};
